<template>
  <modal :open="internalOpen" :width="1000">
    <template #header>
      <modal-header variant="cross" @cancel="closeModal()">
        <h1>{{ $t('pages.driver.tourList.newTour.title') }}</h1>
      </modal-header>
    </template>

    <div class="mt-8">
      <div class="flex gap-x-5 w-full">
        <DatePicker
          v-model="rawTour.createdAt"
          :error="errors?.createdAt"
          :time-accuracy="3"
          mode="datetime"
          :label="$t('pages.driver.tourList.newTour.fields.date')"
        />
        <FormInput
          v-model="rawTour.distance"
          :error="errors?.distance"
          type="number"
          class="w-1/3"
          :icon="icons.km"
          :label="$t('pages.driver.tourList.newTour.fields.distance')"
        />
        <InputDuration
          v-model="rawTour.time"
          :error="errors?.time"
          type="text"
          class="w-1/3"
          :label="$t('pages.driver.tourList.newTour.fields.duration')"
          placeholder="hh:mm:ss"
        />
      </div>
      <div class="flex gap-x-5 w-full mt-7.5">
        <Multiselect
          v-model="rawTour.campaignRegistration"
          :error="errors?.campaignRegistration"
          :options="campaignRegistrations"
          :label="$t('pages.driver.tourList.newTour.fields.campaign')"
          class="w-full"
          :can-deselect="true"
          :can-clear="true"
          :searchable="true"
        />
        <Multiselect
          v-model="rawTour.license"
          :error="errors?.license"
          :options="licenses"
          :label="$t('pages.driver.tourList.newTour.fields.license')"
          class="w-full"
          :can-deselect="true"
          :can-clear="true"
          :searchable="true"
        />
      </div>
      <div class="w-full text-center pt-20">
        <custom-button variant="primary" class="mt-15" :busy="loading" @click="saveNewTour">
          {{ $t('save') }}
        </custom-button>
      </div>
    </div>
  </modal>
</template>

<script lang="tsx" setup>
import { ref, reactive, computed } from 'vue';
import CustomButton from '@components/utils/CustomButton.vue';
import Multiselect from '@components/utils/Multiselect.vue';
import FormInput from '@components/utils/inputs/Input.vue';
import InputDuration from '@components/utils/inputs/InputDuration.vue';
import DatePicker from '@components/utils/inputs/DatePicker.vue';
import { useVModel } from '@vueuse/core';
import { useI18n } from 'vue-i18n';
import { collOptionsProvider } from '@utils/table/hooks/provider';
import { useValidation } from '@hooks/form';
import { date, number, object, string } from 'yup';
import ModalHeader from '@components/utils/Modal/ModalHeader.vue';
import Modal from '@/components/utils/Modal/Modal.vue';
import { useNotify } from '@/components/utils/notifications';
import type { CampaignRegistration, Driver, License } from '@/entities/Entities';
import { useFetchAction } from '@/hooks/fetch';
import { format } from 'date-fns';

interface Props {
  open: boolean;
  driver: Driver;
}

const emit = defineEmits(['update:open', 'saved']);
const props = defineProps<Props>();
const internalOpen = useVModel(props, 'open', emit);

const { t } = useI18n();

const driverFilter = computed(() => ({ driver: props.driver?.id ?? '' }));

const params = reactive({
  filter: driverFilter,
});

const icons = {
  km: <span>Km</span>,
};

const campaignRegistrations = collOptionsProvider<CampaignRegistration>(
  '/campaign_registrations',
  params,
  (value) => {
    const start = value.campaign.startDate ?? value.campaign.group.defaultStartDate;
    const end = value.campaign.endDate ?? value.campaign.group.defaultEndDate;

    const startLabel = start ? format(new Date(start), 'dd.MM.yyyy - HH:mm:ss') : '';
    const endLabel = end ? format(new Date(end), 'dd.MM.yyyy - HH:mm:ss') : '';

    return {
      label: `${value.campaign.group.name} ${startLabel} - ${endLabel}`,
      value: value['@id'],
    };
  },
);

const licenses = collOptionsProvider<License>('/licenses', params, (value) => ({
  label: `${value.company?.name} ${
    value.startDate ? format(new Date(value.startDate), 'dd.MM.yyyy - HH:mm:ss') : ''
  } - ${value.endDate ? format(new Date(value.endDate), 'dd.MM.yyyy - HH:mm:ss') : ''}`,
  value: value['@id'],
}));

const notify = useNotify();

const driver = computed(() => props.driver);

const initialData = {
  driver,
  createdAt: undefined,
  distance: undefined,
  time: undefined,
  campaignRegistration: undefined,
  license: undefined,
};

const rawTour = ref({ ...initialData });

const {
  data: tour,
  errors,
  validatedFn,
  setValidation,
} = useValidation(
  object({
    createdAt: date()
      .nullable()
      .required(t('validation.notEmpty'))
      .test(
        'is-before-now',
        t('validation.beforeToday'),
        (item) => null === item || undefined === item || item < new Date(),
      ),
    distance: number()
      .typeError(t('validation.number'))
      .nullable()
      .required(t('validation.notEmpty'))
      .min(0, t('validation.nonNegative')),
    time: number().nullable().required(t('validation.wrongMask')),
    license: string()
      .nullable()
      .test('not-with-campaign', t('validation.onlyOneOfLicenseOrCampaign'), function (license) {
        const { campaignRegistration } = this.parent;
        if (license && campaignRegistration) {
          return this.createError({
            message: t('validation.onlyOneOfLicenseOrCampaign'),
          });
        }
        return true;
      }),

    campaignRegistration: string()
      .nullable()
      .test(
        'not-with-license',
        t('validation.onlyOneOfLicenseOrCampaign'),
        function (campaignRegistration) {
          const { license } = this.parent;
          if (campaignRegistration && license) {
            return this.createError({
              message: t('validation.onlyOneOfLicenseOrCampaign'),
            });
          }
          return true;
        },
      ),
  }),
  rawTour,
);

function resetData() {
  rawTour.value = { ...initialData } as unknown as (typeof rawTour)['value'];
  setValidation(false);
}

function closeModal() {
  resetData();
  internalOpen.value = false;
}

const [exec, { loading }] = useFetchAction();

const saveNewTour = validatedFn(async () => {
  try {
    await exec('/tours', {
      method: 'POST',
      body: {
        driver: tour.value.driver['@id'],
        campaignRegistration: tour.value.campaignRegistration,
        license: tour.value.license,
        distance: tour.value.distance ? tour.value.distance * 1000 : 0,
        time: tour.value.time,
        createdAt: tour.value.createdAt,
      },
    });

    notify?.({
      variant: 'success',
      title: t('pages.driver.tourList.newTour.saveSuccess'),
    });

    emit('saved');
    closeModal();
  } catch (e) {
    notify?.({
      variant: 'error',
      title: t('pages.driver.tourList.newTour.saveError'),
    });
  }
});
</script>

<style lang="scss" scoped></style>
